import React, { useState, useEffect } from 'react';
import { taskAPI, facilityAPI } from '../services/api';
import { Calendar, CheckCircle, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function Reporting() {
  const [tasks, setTasks] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFacility, setFilterFacility] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => { loadData(); }, [filterFacility, filterStatus]);

  const loadData = async () => {
    try {
      const [tasksRes, facilitiesRes] = await Promise.all([
        taskAPI.getAll({ facilityId: filterFacility, status: filterStatus }),
        facilityAPI.getAll()
      ]);
      // Filter to reporting tasks only
      const allTasks = tasksRes.data.tasks || [];
      const reportingTasks = allTasks.filter(t =>
        t.task_type === 'reporting' ||
        t.task_type === 'permit_renewal'
      );
      setTasks(reportingTasks);
      setFacilities(facilitiesRes.data.facilities || []);
    } catch (error) {
      toast.error('Failed to load reporting schedule');
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async (taskId) => {
    try {
      await taskAPI.update(taskId, { status: 'completed' });
      toast.success('Marked as submitted!');
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getDaysUntil = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate); due.setHours(0,0,0,0);
    return Math.round((due - today) / 86400000);
  };

  const getStatusStyle = (task) => {
    if (task.status === 'completed') return { bg: '#F0FDF4', color: '#16A34A', label: 'Submitted' };
    const days = getDaysUntil(task.due_date);
    if (task.status === 'overdue' || (days !== null && days < 0)) return { bg: '#FEF2F2', color: '#DC2626', label: 'Overdue' };
    if (days !== null && days <= 30) return { bg: '#FFFBEB', color: '#D97706', label: `${days}d` };
    if (days !== null) return { bg: '#EFF6FF', color: '#0066FF', label: `${Math.round(days/30)}mo` };
    return { bg: '#F8FAFC', color: '#6B7280', label: 'Pending' };
  };

  // Group by month
  const grouped = {};
  const now = new Date();

  tasks.forEach(task => {
    let key;
    if (task.status === 'completed') {
      key = 'completed';
    } else if (!task.due_date) {
      key = 'no_date';
    } else {
      const due = new Date(task.due_date);
      key = `${due.getFullYear()}-${due.getMonth()}`;
    }
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(task);
  });

  // Sort keys - upcoming months first, then no_date, then completed
  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    if (a === 'completed') return 1;
    if (b === 'completed') return -1;
    if (a === 'no_date') return 1;
    if (b === 'no_date') return -1;
    return a.localeCompare(b);
  });

  const getGroupLabel = (key) => {
    if (key === 'completed') return 'Submitted';
    if (key === 'no_date') return 'No deadline set';
    const [year, month] = key.split('-').map(Number);
    const d = new Date(year, month);
    const nowMonth = new Date(now.getFullYear(), now.getMonth());
    if (d < nowMonth) return `${MONTH_NAMES[month]} ${year} — Overdue`;
    if (d.getTime() === nowMonth.getTime()) return `${MONTH_NAMES[month]} ${year} — This month`;
    return `${MONTH_NAMES[month]} ${year}`;
  };

  const getGroupColor = (key) => {
    if (key === 'completed') return '#16A34A';
    if (key === 'no_date') return '#94a3b8';
    const [year, month] = key.split('-').map(Number);
    const d = new Date(year, month);
    const nowMonth = new Date(now.getFullYear(), now.getMonth());
    if (d < nowMonth) return '#DC2626';
    if (d.getTime() === nowMonth.getTime()) return '#D97706';
    return '#374151';
  };

  // Stats
  const overdue = tasks.filter(t => t.status === 'overdue' || (getDaysUntil(t.due_date) !== null && getDaysUntil(t.due_date) < 0 && t.status !== 'completed')).length;
  const dueThisMonth = tasks.filter(t => {
    if (t.status === 'completed') return false;
    const days = getDaysUntil(t.due_date);
    return days !== null && days >= 0 && days <= 31;
  }).length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const upcoming = tasks.filter(t => {
    if (t.status === 'completed') return false;
    const days = getDaysUntil(t.due_date);
    return days !== null && days > 31;
  }).length;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6B7280' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="page-title">Reporting</div>
          <div className="page-subtitle">Regulatory submission schedule across all facilities</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { icon: AlertTriangle, bg: '#FEF2F2', color: '#DC2626', value: overdue, label: 'Overdue' },
          { icon: Clock, bg: '#FFFBEB', color: '#D97706', value: dueThisMonth, label: 'Due this month' },
          { icon: ChevronRight, bg: '#EFF6FF', color: '#0066FF', value: upcoming, label: 'Upcoming' },
          { icon: CheckCircle, bg: '#F0FDF4', color: '#16A34A', value: completed, label: 'Submitted' },
        ].map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ width: 34, height: 34, borderRadius: 8, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <s.icon size={16} color={s.color} />
            </div>
            <div className="stat-number" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <select value={filterFacility} onChange={e => setFilterFacility(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', minWidth: 200 }}>
          <option value="">All facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', minWidth: 160 }}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
          <option value="completed">Submitted</option>
        </select>
      </div>

      {/* Empty state */}
      {tasks.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <Calendar size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>No reporting tasks found</div>
          <div style={{ fontSize: 14, color: '#6B7280' }}>No reporting tasks found. In the Tasks page, set task type to "Reporting" or "Permit Renewal" to show tasks here.</div>
        </div>
      )}

      {/* Grouped by month */}
      {sortedKeys.map(key => (
        <div key={key} style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Calendar size={14} color={getGroupColor(key)} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: getGroupColor(key), textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              {getGroupLabel(key)}
            </span>
            <span style={{ background: '#E2E8F0', borderRadius: 20, padding: '1px 8px', fontSize: 12, color: '#6B7280', fontFamily: 'var(--font-body)' }}>
              {grouped[key].length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {grouped[key].map(task => {
              const status = getStatusStyle(task);
              return (
                <div key={task.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: status.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {task.status === 'completed'
                      ? <CheckCircle size={17} color={status.color} />
                      : <Calendar size={17} color={status.color} />
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628', marginBottom: 3 }}>
                      {task.title}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280', fontFamily: 'var(--font-body)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {task.facility_name && <span>📍 {task.facility_name}</span>}
                      {task.due_date && <span>📅 {new Date(task.due_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>}
                      {task.regulation && <span>§ {task.regulation}</span>}
                    </div>
                  </div>
                  <span className="badge" style={{ background: status.bg, color: status.color }}>{status.label}</span>
                  {task.status !== 'completed' && (
                    <button onClick={() => markComplete(task.id)} className="btn btn-ghost" style={{ fontSize: 14, padding: '6px 12px', flexShrink: 0 }}>
                      <CheckCircle size={13} /> Mark Submitted
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Reporting;
